"use client";

import {
  type FC,
  useRef,
  Children,
  type ReactElement,
  useEffect,
  type HTMLAttributes,
  cloneElement,
  useState,
  type RefObject,
  useCallback,
} from "react";
import { AnimatePresence } from "motion/react";
import { mergeClasses, isOutOfViewport } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { ChildrenProp, ClassNameProp } from "../../props";
import { useMaybePubSub } from "../../hooks/usePubSub";
import { useWindowSize } from "../../hooks/useWindowSize";
import { Portal } from "../Portal";
import { mergeRefs } from "../../utils/mergeRefs";
import { nextFrame } from "../../utils/nextFrame";
import { SSRWrapper } from "../SSRWrapper";

import {
  contentRepositionStyle,
  positionerStyle,
} from "./TargetPositioner.css";

export type TargetPlacement =
  | "top"
  | "bottom"
  | "left"
  | "leftTop"
  | "right"
  | "rightTop"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight"
  | "center";

const getOppositePlacement = (
  currentPlacement: TargetPlacement,
  overflow: { top: boolean; bottom: boolean; left: boolean; right: boolean },
): TargetPlacement => {
  if (currentPlacement === "bottomRight") {
    if (overflow.bottom) {
      return "topRight";
    }
    if (overflow.right) {
      return "bottomLeft";
    }
  }
  if (currentPlacement === "bottom") {
    if (overflow.bottom) {
      return "top";
    }
    if (overflow.left) {
      return "bottomRight";
    }
    if (overflow.right) {
      return "bottomLeft";
    }
  }
  if (currentPlacement === "bottomLeft") {
    if (overflow.bottom) {
      return "topLeft";
    }
    if (overflow.left) {
      return "bottomRight";
    }
  }

  if (currentPlacement === "topRight") {
    if (overflow.bottom) {
      return "bottomRight";
    }
    if (overflow.left) {
      return "topLeft";
    }
  }
  if (currentPlacement === "top") {
    if (overflow.top) {
      return "bottom";
    }
    if (overflow.left) {
      return "topRight";
    }
    if (overflow.right) {
      return "topLeft";
    }
  }
  if (currentPlacement === "topLeft") {
    if (overflow.bottom) {
      return "bottomLeft";
    }
    if (overflow.left) {
      return "topRight";
    }
  }

  if (currentPlacement === "right" && overflow.right) {
    return "left";
  }

  if (currentPlacement === "rightTop") {
    if (overflow.right) {
      return "leftTop";
    }
    if (overflow.top) {
      return "right";
    }
  }

  if (currentPlacement === "left" && overflow.left) {
    return "right";
  }

  if (currentPlacement === "leftTop") {
    if (overflow.left) {
      return "rightTop";
    }
    if (overflow.top) {
      return "left";
    }
  }

  return currentPlacement;
};

export type TargetPositionerProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "content" | "active" | "hover" | "focus"
> &
  ChildrenProp &
  RecipeVariants<typeof contentRepositionStyle> &
  ClassNameProp & {
    id?: string;
    placement?: TargetPlacement;
    content?: ReactElement<any>;
    focus?: boolean;
    click?: boolean;
    blur?: boolean;
    hover?: boolean;
    debug?: boolean;
    /**
     * If the mode is fixed, the content will be fixed to the target element right top position.
     * If the mode is reposition, the content will be repositioned based on the placement and the content dimensions.
     */
    mode?: "reposition" | "fixed" | "position";
    zIndex?: number;
    /**
     * If the target positioner is active, it will apply the active state to the target element.
     */
    applyActive?: boolean;
    active?: boolean;
    /**
     * If dimensions are applied, the content will be sized to the target element dimensions.
     */
    applyDimensions?: boolean;
    /**
     * If the target element is a ref, it will be used to position the content.
     */
    targetRef?:
      | RefObject<HTMLElement | null>
      | ((instance: HTMLElement | null) => void)
      | null;
  };

/**
 * TargetPositioner is a component that allows you to position content relative to a target element.
 */
export const TargetPositioner: FC<TargetPositionerProps> = ({
  id,
  placement = "top",
  focus = false,
  hover = false,
  click = false,
  active: isManualActive = false,
  blur = false,
  applyActive = false,
  mode = "reposition",
  applyDimensions = false,
  debug = false,
  targetRef,
  offset,
  content,
  zIndex,
  className,
  children,
}) => {
  const windowSize = useWindowSize();
  const pubSub = useMaybePubSub();

  const childrenArray = Children.toArray(children);
  const firstChild = childrenArray?.[0] as ReactElement<any>;

  const [isActive, setIsActive] = useState(false);
  const [hasActivated, setHasActivated] = useState(
    () => isManualActive === true,
  );
  const isLockedRef = useRef(false);

  if (isManualActive && !hasActivated) {
    setHasActivated(true);
  }

  const targetChildRef = (
    !firstChild && targetRef
      ? targetRef
      : // biome-ignore lint/correctness/useHookAtTopLevel: intentional conditional — ref source is stable per mount
        useRef<HTMLElement>(null)
  ) as RefObject<HTMLElement | null>;
  const targetPositionerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [positionedPlacement, setPositionedPlacement] =
    useState<TargetPlacement>(placement);
  const positionedPlacementRef = useRef<TargetPlacement>(placement);
  const [placementVerified, setPlacementVerified] = useState(false);
  const lockPlacementRef = useRef(false);

  const verifyPlacement = useCallback(() => {
    if (
      lockPlacementRef.current ||
      (windowSize?.height && windowSize.height < 800)
    ) {
      setPlacementVerified(true);
      nextFrame(() => (lockPlacementRef.current = false));
      return;
    }

    const outOfViewport = isOutOfViewport(
      contentRef.current,
      targetChildRef.current,
    );

    // console.log("[TargetPositioner] verifyPlacement", placement, outOfViewport);

    if (
      outOfViewport.top ||
      outOfViewport.bottom ||
      outOfViewport.left ||
      outOfViewport.right
    ) {
      const newPlacement = getOppositePlacement(placement, outOfViewport);
      setPositionedPlacement(newPlacement);
      positionedPlacementRef.current = newPlacement;
      lockPlacementRef.current = true;
    }

    setPlacementVerified(true);
  }, [positionedPlacement, windowSize]);

  const position = useCallback(() => {
    if (
      (!isActive && !isManualActive) ||
      !targetChildRef.current ||
      !targetPositionerRef.current
    ) {
      if (!targetPositionerRef.current) {
        nextFrame(() => position());
      }
      return;
    }

    const targetRect = targetChildRef.current.getBoundingClientRect();
    let top = 0,
      left = 0;

    switch (positionedPlacementRef.current) {
      case "top":
        top = targetRect.top;
        left = targetRect.left + targetRect.width / 2;
        break;
      case "bottom":
        top = targetRect.bottom;
        left = targetRect.left + targetRect.width / 2;
        break;
      case "left":
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left;
        break;
      case "leftTop":
        top = targetRect.top;
        left = targetRect.left;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.right;
        break;
      case "rightTop":
        top = targetRect.top + targetRect.height;
        left = targetRect.right;
        break;
      case "topLeft":
        top = targetRect.top + targetRect.height;
        left = targetRect.left + targetRect.width;
        break;
      case "topRight":
        top = targetRect.top;
        left = targetRect.left;
        break;
      case "bottomLeft":
        top = targetRect.bottom;
        left = targetRect.left + targetRect.width;
        break;
      case "bottomRight":
        top = targetRect.bottom;
        left = targetRect.left;
        break;
      case "center":
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left + targetRect.width / 2;
        break;
    }

    targetPositionerRef.current.style.top = `${top}px`;
    targetPositionerRef.current.style.left = `${left}px`;

    if (applyDimensions) {
      targetPositionerRef.current.style.width = `${targetRect.width}px`;
      targetPositionerRef.current.style.height = `${targetRect.height}px`;
    }

    nextFrame(() => verifyPlacement());
  }, [isActive, isManualActive, positionedPlacement, verifyPlacement]);

  useEffect(() => {
    if (!(isActive || isManualActive)) {
      setPlacementVerified(false);
    }

    if (!(isActive || isManualActive) || !pubSub) {
      return;
    }

    setPositionedPlacement(placement);
    positionedPlacementRef.current = placement;

    const unsubscribe = pubSub.subscribe("onScroll", () => position());

    return () => unsubscribe();
  }, [placement, isActive, isManualActive]);

  useEffect(() => {
    position();
  }, [placement, isManualActive, isActive, positionedPlacement, windowSize]);

  const handleActive = () => {
    if (isLockedRef.current) {
      return;
    }
    if (!hasActivated) setHasActivated(true);
    setIsActive(true);
  };

  const handleInactive = () => {
    if (click && isLockedRef.current) {
      return;
    }
    setIsActive(false);
    isLockedRef.current = false;
  };

  const handleClick = () => {
    if (click) {
      if (isLockedRef.current) {
        setIsActive(false);
        isLockedRef.current = false;
      } else {
        if (!hasActivated) setHasActivated(true);
        setIsActive(true);
        isLockedRef.current = true;
      }
    } else {
      setIsActive(false);
      isLockedRef.current = true;
    }
  };

  useEffect(() => {
    if (!targetChildRef.current) {
      return;
    }

    if (typeof targetChildRef.current.addEventListener !== "function") {
      console.warn(
        "[TargetPositioner] targetChildRef.current.addEventListener is not a function",
        targetChildRef.current,
      );
      return;
    }

    targetChildRef.current.addEventListener("click", handleClick);

    if (hover) {
      targetChildRef.current.addEventListener("mouseenter", handleActive);
      targetChildRef.current.addEventListener("mouseleave", handleInactive);
    }
    if (focus) {
      targetChildRef.current.addEventListener("focusin", handleActive);
    }
    if (focus || blur) {
      targetChildRef.current.addEventListener("blur", handleInactive);
    }

    return () => {
      if (!targetChildRef.current) {
        return;
      }

      if (typeof targetChildRef.current.addEventListener !== "function") {
        console.warn(
          "[TargetPositioner] targetChildRef.current.addEventListener is not a function",
          targetChildRef.current,
        );
        return;
      }

      targetChildRef.current?.removeEventListener("mouseenter", handleActive);
      targetChildRef.current?.removeEventListener("mouseleave", handleInactive);
      targetChildRef.current?.removeEventListener("click", handleClick);
      targetChildRef.current?.removeEventListener("focusin", handleActive);
      targetChildRef.current?.removeEventListener("blur", handleInactive);
    };
  }, [hover, focus, blur]);

  useEffect(() => {
    pubSub?.subscribe("recalculateLayout", () => {
      position();
    });
  }, [pubSub]);

  // console.log("(!!!) [TargetPositioner] RENDER", isActive);

  return (
    <>
      {firstChild &&
        cloneElement(firstChild, {
          ref: mergeRefs(targetRef, targetChildRef),
          "aria-describedby": id,
          ...(applyActive ? { active: isActive || isManualActive } : {}),
        })}
      {childrenArray.slice(1)}

      {(hasActivated || isManualActive) && (
        <SSRWrapper>
          <Portal>
            <div
              id={id}
              ref={targetPositionerRef}
              className={mergeClasses(
                positionerStyle({ placement: positionedPlacement, debug }),
                className,
              )}
              style={{ zIndex, opacity: placementVerified ? 1 : 0 }}
            >
              <AnimatePresence>
                {!!content &&
                  (isActive || isManualActive) &&
                  (mode === "reposition" ? (
                    <div>
                      <div
                        ref={contentRef}
                        className={contentRepositionStyle({
                          placement: positionedPlacement,
                          offset,
                        })}
                      >
                        {content}
                      </div>
                    </div>
                  ) : (
                    <div>{content}</div>
                  ))}
              </AnimatePresence>
            </div>
          </Portal>
        </SSRWrapper>
      )}
    </>
  );
};
